import React, { useEffect, useState } from "react";
import { Card, Row, Col, Space, Avatar, Typography, Divider, Spin, Empty, Button, Modal } from "antd";
import { 
  ThunderboltOutlined, 
  TrophyOutlined, 
  RocketOutlined,
  StarOutlined,
  PlusOutlined,
  EyeOutlined
} from "@ant-design/icons";
import { CustomForm } from "../../../../Components/Admin/CustomForm";
import { EvolucionesList } from "./Components/EvolucionesList";
import { useSelectLogro } from "../../../../Hooks/Admin";
import { useSelectPokemon } from "../../../../Hooks/Admin";
import {
  useAsignarRequerimientoEvolucion,
  useSelectRequerimientoPokemon,
  useListarEvolucionesPokemon,
  useEliminarEvolucionPokemon,
} from "../queries/useRequerimientoEvolucion";
import { camposAsignarRequerimiento } from "./Helpers/requerimientoEvolucionHelper";

const { Title, Text } = Typography;

export const RequerimientoEvolucion = ({ id }) => {
  const queryRequerimientos = useSelectRequerimientoPokemon();
  const queryLogros = useSelectLogro();
  const queryPokemones = useSelectPokemon();
  const queryEvoluciones = useListarEvolucionesPokemon({ idPokemon: id, page: 1, perPage: 100 });
  const queryEliminar = useEliminarEvolucionPokemon();
  
  const [campos, setCampos] = useState();
  const [showAddForm, setShowAddForm] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    setCampos(
      camposAsignarRequerimiento(
        queryRequerimientos.data ?? [],
        queryLogros.data ?? [],
        queryPokemones.data ?? [],
        id
      )
    );
  }, [queryRequerimientos.data, queryLogros.data, queryPokemones.data, id]);

  const modificaSubmit = (data) => ({ ...data, idPokemon: id });

  // Función para manejar la eliminación de evoluciones
  const handleDeleteEvolucion = async (evolucionId) => {
    try {
      setDeletingId(evolucionId);
      await queryEliminar.mutateAsync(evolucionId);
      // Refrescar la lista después de eliminar
      queryEvoluciones.refetch();
    } catch (error) {
      console.error('Error al eliminar evolución:', error);
    } finally {
      setDeletingId(null);
    }
  };

  // Callback para refrescar después de agregar
  const handleRefetchAfterAdd = () => {
    setShowAddForm(false);
    queryEvoluciones.refetch();
  };

  if (!id) {
    return (
      <Card
        style={{
          borderRadius: '12px',
          border: '1px solid #f0f0f0',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Text type="secondary">
              Seleccione un Pokémon para configurar sus requerimientos de evolución
            </Text>
          }
        />
      </Card>
    );
  }

  if (queryRequerimientos.isLoading || queryLogros.isLoading || queryPokemones.isLoading || !campos) {
    return (
      <Card
        style={{
          borderRadius: '12px',
          border: '1px solid #f0f0f0',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          textAlign: 'center',
          padding: '40px 20px'
        }}
      >
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text type="secondary">Cargando requerimientos de evolución...</Text>
        </div>
      </Card>
    );
  }

  return (
    <Card
      style={{
        borderRadius: '12px',
        border: '1px solid #f0f0f0',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}
    >
      {/* Header Card */}
      <div
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          margin: '-24px -24px 24px -24px',
          padding: '24px',
          color: 'white',
          borderRadius: '12px 12px 0 0',
        }}
      >
        <Row align="middle" gutter={[16, 0]}>
          <Col>
            <Avatar
              size={64}
              style={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                border: '2px solid rgba(255,255,255,0.3)'
              }}
              icon={<RocketOutlined />}
            />
          </Col>
          <Col flex={1}>
            <Title
              level={3}
              style={{
                margin: 0,
                color: 'white',
                fontWeight: '600'
              }}
            >
              Requerimientos de Evolución
            </Title>
            <Text style={{
              color: 'rgba(255,255,255,0.8)',
              fontSize: '14px'
            }}>
              Gestione las evoluciones del Pokémon
            </Text>
          </Col>
          <Col>
            <Space>
              <ThunderboltOutlined style={{ fontSize: '24px', color: '#ffa940' }} />
              <TrophyOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
              <StarOutlined style={{ fontSize: '24px', color: '#faad14' }} />
            </Space>
          </Col>
        </Row>
      </div>

      {/* Content */}
      <div style={{ padding: '0 8px' }}>
        <Divider orientation="left">
          <Space>
            <EyeOutlined style={{ color: '#52c41a' }} />
            <span>Evoluciones Actuales</span>
          </Space>
        </Divider>

        <Card
          style={{
            backgroundColor: '#fafafa',
            border: '1px solid #e8e8e8',
            borderRadius: '8px',
            marginBottom: '24px'
          }}
          bodyStyle={{ padding: '20px' }}
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setShowAddForm(true)}
              style={{ borderRadius: '6px' }}
            >
              Agregar Evolución
            </Button>
          }
        >
          <EvolucionesList
            evoluciones={queryEvoluciones?.data || []}
            loading={queryEvoluciones.isLoading}
            onDeleteEvolucion={handleDeleteEvolucion}
            deletingId={deletingId}
          />
        </Card>

        <Modal
          title={
            <Space>
              <RocketOutlined style={{ color: '#1890ff' }} />
              <span>Agregar Nueva Evolución</span>
            </Space>
          }
          open={showAddForm}
          onCancel={() => setShowAddForm(false)}
          footer={null}
          width={800}
          destroyOnClose={true}
        >
          <Card
            style={{
              backgroundColor: '#fafafa',
              border: '1px solid #f0f0f0',
              borderRadius: '8px',
              marginTop: '16px'
            }}
            bodyStyle={{ padding: '24px' }}
          >
            <CustomForm
              camposFormulario={campos}
              querySubmit={useAsignarRequerimientoEvolucion}
              modificaSubmit={modificaSubmit}
              setRefetch={handleRefetchAfterAdd}
            />
          </Card>
        </Modal>
      </div>
    </Card>
  );
};
